import { useState } from 'react'
import { Dialog } from 'radix-ui'
import { Combobox } from '@/components/common/Combobox'
import type { StatusBadgeVariant } from '@/components/common/StatusBadge'
import { useCreateStage, useUpdateStage } from '../hooks'
import type { Stage, StageOwner } from '../types'
import { STAGE_CODE_REGEX, formatError } from './formatError'

const COLOR_OPTIONS: Array<{ value: StatusBadgeVariant; label: string; prefix: React.ReactNode }> = [
  { value: 'neutral', label: 'Neutralny (szary)',            prefix: <span className="size-3 rounded bg-slate-100 border border-slate-300 shrink-0" /> },
  { value: 'info',    label: 'Informacyjny (niebieski)',      prefix: <span className="size-3 rounded bg-sky-100 border border-sky-300 shrink-0" /> },
  { value: 'success', label: 'Sukces (zielony)',              prefix: <span className="size-3 rounded bg-emerald-100 border border-emerald-300 shrink-0" /> },
  { value: 'warning', label: 'Ostrzeżenie (pomarańczowy)',   prefix: <span className="size-3 rounded bg-amber-100 border border-amber-300 shrink-0" /> },
  { value: 'danger',  label: 'Niebezpieczeństwo (czerwony)', prefix: <span className="size-3 rounded bg-red-100 border border-red-300 shrink-0" /> },
]

export function StageFormDialog({ owner, editing, defaultSortOrder, onClose }: {
  owner: string | StageOwner
  editing: Stage | null
  defaultSortOrder: number
  onClose: () => void
}) {
  const create = useCreateStage(owner)
  const update = useUpdateStage(owner)
  const [code, setCode] = useState(editing?.code ?? '')
  const [labelPl, setLabelPl] = useState(editing?.labelPl ?? '')
  const sortOrder = editing?.sortOrder ?? defaultSortOrder
  const [isTerminal, setIsTerminal] = useState(editing?.isTerminal ?? false)
  const [color, setColor] = useState<StatusBadgeVariant>(editing?.color ?? 'neutral')
  const [error, setError] = useState<string | null>(null)

  const submit = () => {
    setError(null)
    if (!editing && !STAGE_CODE_REGEX.test(code)) {
      setError('Code: WIELKIE LITERY, cyfry, podkreślenia. Musi zaczynać się od litery.')
      return
    }
    if (!labelPl.trim()) { setError('Etykieta nie może być pusta.'); return }

    if (editing) {
      update.mutate(
        { code: editing.code, payload: { labelPl, sortOrder, isTerminal, color } },
        { onSuccess: onClose, onError: e => setError(formatError(e)) },
      )
    } else {
      create.mutate(
        { code, labelPl, sortOrder, isTerminal, color },
        { onSuccess: onClose, onError: e => setError(formatError(e)) },
      )
    }
  }

  const isSubmitting = create.isPending || update.isPending

  return (
    <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-5 w-[440px] shadow-xl">
          <Dialog.Title className="text-base font-semibold">
            {editing ? `Edycja etapu ${editing.code}` : 'Nowy etap'}
          </Dialog.Title>
          <Dialog.Description className="text-xs text-slate-500 mt-1">
            Code: WIELKIE_LITERY, cyfry, podkreślenia. Używany w API i wymaganiach przejść.
          </Dialog.Description>

          <div className="mt-4 flex flex-col gap-3 text-sm">
            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Kod</span>
              <input
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase())}
                disabled={!!editing || isSubmitting}
                className="border rounded px-2 py-1 font-mono"
                placeholder="DRAFT, MONTAZ, ..."
              />
            </label>
            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Etykieta</span>
              <input value={labelPl} onChange={e => setLabelPl(e.target.value)} disabled={isSubmitting} className="border rounded px-2 py-1" />
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={isTerminal} onChange={e => setIsTerminal(e.target.checked)} disabled={isSubmitting} />
              <span>Etap końcowy (terminal)</span>
            </label>
            <div className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Kolor</span>
              <Combobox<StatusBadgeVariant>
                value={color}
                onChange={v => setColor(v ?? 'neutral')}
                options={COLOR_OPTIONS}
                disabled={isSubmitting}
                searchThreshold={10}
              />
            </div>
            <p className="text-[10px] text-slate-400">Kolejność: przeciągnij etapy na liście.</p>

            {error && <p className="text-red-600 text-xs">{error}</p>}

            <div className="flex justify-end gap-2 mt-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded" disabled={isSubmitting}>Anuluj</button>
              <button type="button" onClick={submit} className="px-3 py-1.5 bg-sky-600 text-white rounded disabled:opacity-50" disabled={isSubmitting}>
                {isSubmitting ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
